import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { getActiveAnnouncements } from '../api/announcements';

const DEFAULT_INTERVAL = 8000;
const BASE_SLIDE_DURATION = 400;

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const SLIDE_DURATION = prefersReducedMotion ? 0 : BASE_SLIDE_DURATION;

const variantStyles = {
  info:    { background: '#dbeafe', color: '#1e40af' },
  warning: { background: '#fef3c7', color: '#92400e' },
  success: { background: '#d1fae5', color: '#065f46' },
  promo:   { background: '#ede9fe', color: '#5b21b6' },
};

const ALLOWED_TAGS = ['a', 'strong', 'em', 'b', 'i', 'span', 'br'];
const sanitize = (html) => DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'] });

const isValidUrl = (url) => {
  try { const u = new URL(url); return u.protocol === 'http:' || u.protocol === 'https:'; }
  catch { return false; }
};

export default function AnnouncementBar() {
  const { t } = useTranslation('common');
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideState, setSlideState] = useState('visible');
  const [dismissed, setDismissed] = useState(() => {
    try {
      const session = JSON.parse(sessionStorage.getItem('dismissed_announcements') || '[]');
      const permanent = JSON.parse(localStorage.getItem('dismissed_announcements_permanent') || '[]');
      return { session, permanent };
    } catch { return { session: [], permanent: [] }; }
  });
  const timeoutRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getActiveAnnouncements().then(items => {
      if (!cancelled) setAnnouncements(items);
    });
    return () => { cancelled = true; };
  }, []);

  const visible = announcements.filter(a => !dismissed.session.includes(a.id) && !dismissed.permanent.includes(a.id));

  const scheduleRotation = useCallback(() => {
    if (visible.length <= 1) return;
    if (pausedRef.current) return;

    const safeIdx = currentIndex % visible.length;
    const current = visible[safeIdx];
    const interval = ((current?.rotation_interval || 8) * 1000) || DEFAULT_INTERVAL;

    timeoutRef.current = setTimeout(() => {
      if (pausedRef.current) return;
      setSlideState('slide-out');
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % visible.length);
        setSlideState('slide-in');
        setTimeout(() => setSlideState('visible'), SLIDE_DURATION > 0 ? 50 : 0);
      }, SLIDE_DURATION);
    }, interval);
  }, [visible, currentIndex]);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    scheduleRotation();
    return () => clearTimeout(timeoutRef.current);
  }, [scheduleRotation, slideState]);

  const handlePause = useCallback(() => {
    pausedRef.current = true;
    clearTimeout(timeoutRef.current);
  }, []);

  const handleResume = useCallback(() => {
    pausedRef.current = false;
    scheduleRotation();
  }, [scheduleRotation]);

  const dismiss = useCallback((announcement) => {
    if (announcement.dismiss_mode === 'permanent') {
      const next = [...dismissed.permanent, announcement.id];
      setDismissed(prev => ({ ...prev, permanent: next }));
      localStorage.setItem('dismissed_announcements_permanent', JSON.stringify(next));
    } else {
      const next = [...dismissed.session, announcement.id];
      setDismissed(prev => ({ ...prev, session: next }));
      sessionStorage.setItem('dismissed_announcements', JSON.stringify(next));
    }
  }, [dismissed]);

  if (visible.length === 0) return null;

  const safeIndex = currentIndex % visible.length;
  const current = visible[safeIndex];
  const style = variantStyles[current.variant] || variantStyles.info;

  const slideTransform =
    slideState === 'slide-out' ? 'translateY(-100%)' :
    slideState === 'slide-in'  ? 'translateY(100%)' :
    'translateY(0)';

  const slideOpacity =
    slideState === 'slide-out' || slideState === 'slide-in' ? 0 : 1;

  return (
    <div
      className="announcement-bar"
      role="region"
      aria-live="polite"
      aria-label={t('announcements.region', 'Announcements')}
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
      onFocus={handlePause}
      onBlur={handleResume}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 48px 10px 20px',
        fontSize: '13px',
        fontWeight: 500,
        position: 'relative',
        minHeight: '40px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: slideTransform,
          opacity: slideOpacity,
          transition: slideState === 'slide-in'
            ? 'none'
            : SLIDE_DURATION > 0
              ? `transform ${SLIDE_DURATION}ms ease, opacity ${SLIDE_DURATION}ms ease`
              : 'none',
        }}
      >
        <span
          style={{ flex: '0 1 auto', textAlign: 'center' }}
          dangerouslySetInnerHTML={{ __html: sanitize(current.message) }}
        />

        {current.link_url && isValidUrl(current.link_url) && (
          <a
            href={current.link_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'inherit',
              marginLeft: 12,
              fontWeight: 700,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              whiteSpace: 'nowrap',
            }}
          >
            {current.link_label || t('announcements.learnMore')}
          </a>
        )}
      </div>

      {current.dismiss_mode !== 'none' && (
        <button
          onClick={() => dismiss(current)}
          aria-label={t('announcements.dismiss')}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
            opacity: 0.7,
            padding: '4px',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
